import { PropTypes } from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AccionaButton from '../../../../../components/AccionaButton';
import AccionaTable from '../../../../../components/AccionaTable';
import AccionaUploadButton from '../../../../../components/AccionaUploadButton';
import RemoveModal from '../../../../../components/RemoveModal/index';
import {
  deliveryNotesFileExtensions,
  laboratoryFileExtensions,
} from '../../../../../constants/general';
import { MainContext } from '../../../../../context/MainContext';
import { createFile } from '../../../../../services/api/files';
import * as PublicTemplatesActions from '../../../../../services/redux/publicTemplates/actions';
import * as ProjectTemplatesActions from '../../../../../services/redux/projectTemplates/actions';
import { BUTTONS, COLUMNS_HEADERS } from './columns';
import AccionaTableButton from '../../../../../components/AccionaTable/AccionaTableButton';
import { generateFileName } from '../../../../../utils/utils';
import AccionaTableTabs from '../../../../../components/AccionaTableTabs';
import { NotificationsContext } from '../../../../../context/NotificationsContext';

const Tabs = {
  PROJECT: 'projectTemplates',
  PUBLIC: 'publicTemplates',
};

const TemplatesList = ({
  getProjectTemplateList,
  getPublicTemplateList,
  deletePublicTemplate,
  deleteProjectTemplate,
  errorMessages,
}) => {
  const intl = useIntl();
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const { setLoading, setError } = useContext(MainContext);
  const [selectedTab, setSelectedTab] = useState(Tabs.PROJECT);
  const dispatch = useDispatch();
  const { isCopied } = useSelector(({ publicTemplates }) => publicTemplates);
  const { pushNotification } = useContext(NotificationsContext);

  const fileSelectedHandler = (event, model) => {
    const file = event.target.files[0];
    const extension = file.name.split('.').pop().toLowerCase();
    const fileExtensions =
      model === 'delivery'
        ? deliveryNotesFileExtensions
        : laboratoryFileExtensions;
    if (file) {
      // Check that the file entered is correct
      if (fileExtensions.includes(extension)) {
        setLoading(true);
        const filename = generateFileName(
          'template',
          model,
          file.name.split('.').pop().toLowerCase(),
        );
        createFile({ file, model, filename })
          .then((n) => {
            // Create and store the file
            setLoading(false);
            navigate(
              `/app/project/${id}/quality/ocr/templates/create/${model}/${filename}`,
            );
          })
          .catch((error) => {
            setLoading(false);
            setError({
              status: error.response ? error.response.status : 502,
              errorType: 'UPLOAD_FILE_ERROR',
            });
          });
      } else {
        setError({
          status: 415,
          errorType: 'UPLOAD_FILE_ERROR',
        });
      }
    }
  };

  const handleOnDelete = (template) => {
    if (template) {
      setSelectedRows([template]);
    }
    setShowRemoveModal(true);
  };
  const handleOnEdit = (template) => {
    navigate(
      `/app/project/${id}/quality/ocr/templates/update/${template.model}/${template.image}/${template.id}`,
    );
  };

  const handleOnBulkDeleting = () => {
    setShowRemoveModal(false);
    selectedRows.forEach((row) => {
      if (selectedTab === Tabs.PROJECT) {
        deleteProjectTemplate(row.id);
      } else {
        deletePublicTemplate(row.id);
      }
    });
    setSelectedRows([]);
  };

  const onTabChange = (tab) => {
    setSelectedTab(tab);
    setSelectedRows([]);
  };

  const handleOnCopy = (template) => {
    dispatch(
      PublicTemplatesActions.copyPublicTemplate({
        pid: id,
        id: template.id,
        name: template.name + ' COPY',
      }),
    );
  };

  useEffect(() => {
    if (isCopied) {
      pushNotification({
        message: intl.formatMessage({ id: 'templates.copy.success' }),
        type: 'success',
        callback: () => {
          dispatch(PublicTemplatesActions.clearCopied());
        },
      });
    }
  }, [dispatch, intl, isCopied, pushNotification]);

  useEffect(() => {
    if (errorMessages) {
      setError(errorMessages);
    }
  }, [errorMessages, setError]);
  return (
    <>
      <AccionaTableTabs
        selectedRows={selectedRows}
        selectedTab={selectedTab}
        setSelectedTab={onTabChange}
        handleOnBulkDeleting={handleOnBulkDeleting}
        tabs={[Tabs.PROJECT, Tabs.PUBLIC]}
        title={intl.formatMessage({ id: 'templates' })}
        onBackRoute={`/app/project/${id}/quality/ocr`}
        actions={
          <>
            <AccionaUploadButton
              type="deliveryNote"
              onClick={(event) => fileSelectedHandler(event, 'delivery')}
              msg="button.create.delivery"
              id="deliveryUploadButton"
            />
            <AccionaUploadButton
              type="laboratory"
              onClick={(event) => fileSelectedHandler(event, 'laboratory')}
              msg="button.create.laboratory"
              id="laboratoryUploadButton"
            />
            <AccionaButton
              id="deleteBtn"
              htmlType="submit"
              disabled={!selectedRows.length}
              onClick={() => handleOnDelete()}
              msg={intl.formatMessage({ id: 'button.delete' })}
              classIcon="icon fas fa-trash-alt"
            />
          </>
        }
        render={() => {
          switch (selectedTab) {
            case Tabs.PROJECT:
              return (
                <AccionaTable
                  showTitle={false}
                  allowClear
                  key={Tabs.PROJECT}
                  columns={COLUMNS_HEADERS}
                  getItems={getProjectTemplateList}
                  entity="projectTemplates"
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  isRowChecked={(record) => {
                    return selectedRows.some((r) => r.id === record.id);
                  }}
                  actions={(template) => {
                    return (
                      <>
                        <AccionaTableButton
                          onClick={() => handleOnEdit(template)}
                          type="edit"
                          toolTip={intl.formatMessage({
                            id: BUTTONS.editAction.tooltip,
                          })}
                        />
                        <AccionaTableButton
                          onClick={() => handleOnDelete(template)}
                          type="delete"
                          toolTip={intl.formatMessage({
                            id: BUTTONS.deleteAction.tooltip,
                          })}
                        />
                      </>
                    );
                  }}
                />
              );
            case Tabs.PUBLIC:
              return (
                <AccionaTable
                  key={Tabs.PUBLIC}
                  columns={COLUMNS_HEADERS}
                  getItems={getPublicTemplateList}
                  entity="publicTemplates"
                  showTitle={false}
                  actions={(template) => (
                    <>
                      <AccionaTableButton
                        type="copy"
                        onClick={() => handleOnCopy(template)}
                        toolTip={intl.formatMessage({
                          id: BUTTONS.copyAction.tooltip,
                        })}
                      />
                    </>
                  )}
                />
              );
            default:
              return null;
          }
        }}
      />
      <RemoveModal
        entity="templates"
        selectedRows={selectedRows}
        msg={intl.formatMessage({
          id: selectedRows.length > 1 ? 'delete.templates' : 'delete.template',
        })}
        onOk={handleOnBulkDeleting}
        onCancel={() => setShowRemoveModal((prev) => !prev)}
        hidden={!showRemoveModal}
      />
    </>
  );
};

const mapStateToProps = ({ publicTemplates, projectTemplates }) => ({
  publicTemplates: publicTemplates.list,
  publicTemplatesFetching: publicTemplates.fetching,
  publicTemplatesErrorMessages: publicTemplates.errorMessages,
  projectTemplates: projectTemplates.list,
  projectTemplatesFetching: projectTemplates.fetching,
  projectTemplatesErrorMessages: projectTemplates.errorMessages,
});

const matchDispachToProps = {
  getPublicTemplateList: PublicTemplatesActions.getPublicTemplates,
  getProjectTemplateList: ProjectTemplatesActions.getProjectTemplates,
  deletePublicTemplate: PublicTemplatesActions.deletePublicTemplate,
  deleteProjectTemplate: ProjectTemplatesActions.deleteProjectTemplate,
  clearErrors: PublicTemplatesActions.clearErrors,
};

TemplatesList.defaultProps = {
  errorMessages: undefined,
};

TemplatesList.propTypes = {
  getPublicTemplateList: PropTypes.func.isRequired,
  getProjectTemplateList: PropTypes.func.isRequired,
  deleteProjectTemplate: PropTypes.func.isRequired,
  deletePublicTemplate: PropTypes.func.isRequired,
  errorMessages: PropTypes.shape({
    status: PropTypes.number,
  }),
};

export default connect(mapStateToProps, matchDispachToProps)(TemplatesList);
